import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMessage, markMessage, deleteMessage, markSpam, folderQs } from '@/api/messages';
import { UnsubscribeModal } from './unsubscribe-modal';
import { createFilter } from '@/api/filters';
import { sanitizeEmailHtml, stripHtml } from '@/lib/sanitize';
import { linkifyEmailsAndPhones, findContacts } from '@/lib/linkify';
import { contactFromHref, composeToUrl, type ContactLink } from '@/lib/contact-link';
import { ContactActionPopover } from './contact-action-popover';
import { useUi } from './ui-dialogs';
import { ForwardPickerModal } from './forward-picker-modal';
import { API_BASE_URL } from '@/api/client';
import { opensInline } from '@/lib/attachment-open';

// Download via fetch + blob so the httpOnly auth cookie travels with the request.
// Plain <a href> links fail in the Capacitor/Android shell: the system download
// manager re-requests the URL without cookies and gets a 401 ("Needs authorization").
//
// iOS exception (2026-09-01): Safari/WebView ignores programmatic blob-anchor
// downloads — the share sheet / "Save to OneDrive" receives nothing and the save
// silently fails. On iOS a real navigation to the endpoint is the reliable path:
// cookies travel with top-level navigations, and the server sets
// Content-Disposition (with the true filename) so Safari routes it to the file
// viewer / share sheet properly.
const IS_IOS = typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const downloadAttachment = async (mailboxId: string, uid: number, index: number, filename?: string, folder?: string | null) => {
  try {
    if (IS_IOS && !(window as unknown as { Capacitor?: unknown }).Capacitor) {
      // iOS/standalone PWA: fetch the bytes (cookie travels), then hand Safari a
      // blob window. QuickLook renders PDFs natively with a working Share → Save
      // to Files/OneDrive sheet. Direct navigation works too, but in standalone
      // mode Content-Disposition: attachment can bounce to Safari awkwardly.
      const serverUrl = `${API_BASE_URL}/mailboxes/${mailboxId}/messages/${uid}/attachments/${index}${folderQs(folder)}`;
      const res = await fetch(serverUrl, { credentials: 'include' });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      // B-02: a blob window runs in b-mail's origin, so an HTML/SVG attachment would run as the
      // user. Only inert types open here; the rest navigate to the server, which downloads them.
      if (!opensInline(blob.type)) { window.location.href = serverUrl; return; }
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        // Popup blocked — fall back to navigation; the server sends proper headers.
        window.location.href = serverUrl;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
    const res = await fetch(`${API_BASE_URL}/mailboxes/${mailboxId}/messages/${uid}/attachments/${index}${folderQs(folder)}`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `attachment-${index}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (err) {
    console.error('attachment download failed:', err);
    alert("Couldn't download attachment — try signing out and back in, then retry.");
  }
};
import { formatDateTime } from '@/lib/utils';
import { ArrowLeft, Reply, ReplyAll, Forward, Trash2, Flag, MailOpen, ShieldCheck, ShieldAlert, Ban, CheckCircle2, Download, MailX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMailStore } from '@/stores/mail';
import { useUIStore } from '@/stores/ui';
import { useState, useEffect, useRef } from 'react';

// to_email / cc_email are stored as JSON arrays of addresses (e.g. ["a@b.com"]).
// Render them as a clean comma-separated list; return '' when there are none so
// the row can be hidden (bulk/Bcc mail often has no To recipient at all).
function formatAddrs(v?: string | null): string {
  if (!v) return '';
  try {
    const arr = JSON.parse(v);
    if (Array.isArray(arr)) return arr.filter(Boolean).join(', ');
  } catch {
    /* not JSON — fall through to raw */
  }
  return String(v);
}

// spam_symbols is a JSON object of the top Rspamd symbols that added score, e.g.
// {"BAYES_SPAM":3.5,"DMARC_POLICY_SOFTFAIL":2.0}. Render it human-readably.
function formatSymbols(v?: string | null): string {
  if (!v) return '';
  try {
    const o = JSON.parse(v);
    return Object.entries(o)
      .map(([n, s]) => `${n} (+${Number(s).toFixed(1)})`)
      .join(', ');
  } catch {
    return '';
  }
}

// Plain-text mail gets the same phone/address links as HTML mail, built by React rather than by
// linkify's DOM walk: React owns this text node and would keep updating a detached copy of it.
function withContactLinks(text: string) {
  const out: React.ReactNode[] = [];
  let at = 0;
  for (const m of findContacts(text)) {
    if (m.start > at) out.push(text.slice(at, m.start));
    out.push(<a key={m.start} href={m.href} className="bmail-linkified">{text.slice(m.start, m.end)}</a>);
    at = m.end;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

interface MessageViewProps {
  mailboxId: string | null;
  uid: number | null;
  // IMAP folder the row was listed in. UIDs are only unique per folder, so
  // without it a Junk message can resolve to an unrelated INBOX message.
  folder?: string | null;
  // Called after delete / spam / not-spam so the PARENT decides where to go
  // (stay in the current mailbox/folder) instead of always jumping to /inbox.
  onActioned?: () => void;
}

export function MessageView({ mailboxId, uid, folder = null, onActioned }: MessageViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ui = useUi();
  const loadImagesByDefault = useUIStore((s) => s.loadImagesByDefault);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [showUnsubscribe, setShowUnsubscribe] = useState(false);
  const [unsubPending, setUnsubPending] = useState(false);
  // A tapped phone number or address in the body, and the address an email is being forwarded to.
  const [contact, setContact] = useState<{ link: ContactLink; anchor: { x: number; top: number; bottom: number } } | null>(null);
  const [forwardTo, setForwardTo] = useState<string | null>(null);
  // Copy a street address from the maps menu (2026-10-30). navigator.clipboard is the
  // standard path (https context); the textarea fallback covers a WebView that reports
  // it unavailable. The toast confirms — a silent copy looks like a no-op.
  const copyAddress = async (address: string) => {
    setContact(null);
    try {
      await navigator.clipboard.writeText(address);
      ui.toast({ kind: 'success', message: 'Address copied' });
    } catch {
      const ta = document.createElement('textarea');
      ta.value = address;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      if (ok) ui.toast({ kind: 'success', message: 'Address copied' });
      else ui.toast({ kind: 'error', message: 'Could not copy the address' });
    }
  };
  // Reset the per-message "images loaded" state whenever a different message opens.
  useEffect(() => { setImagesLoaded(false); }, [mailboxId, uid, folder]);
  const { data, isLoading, error } = useQuery({
    queryKey: ['message', mailboxId, uid, folder],
    queryFn: () => (mailboxId && uid !== null ? getMessage(mailboxId, uid, folder) : Promise.resolve(null)),
    enabled: !!mailboxId && uid !== null
  });

  const markMutation = useMutation({
    mutationFn: (updates: { isRead?: boolean; isFlagged?: boolean }) =>
      mailboxId && uid !== null ? markMessage(mailboxId, uid, updates, folder) : Promise.reject(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => (mailboxId && uid !== null ? deleteMessage(mailboxId, uid, folder) : Promise.reject()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      onActioned?.();
    }
  });

  // Mark as spam: train Rspamd as spam + block sender (unless it's the mailbox owner's own address — spoofed From headers)
  const spamMutation = useMutation({
    mutationFn: async () => {
      if (!mailboxId || uid === null || !data?.message?.fromEmail) return Promise.reject();
      const fromEmail = data.message.fromEmail;
      await markSpam(mailboxId, uid, true, folder);
      // Don't block the mailbox owner's own address — spam often spoofs the From header to match the recipient
      const mailboxOwner = mailboxes.find((m) => m.id === mailboxId)?.email;
      if (fromEmail !== mailboxOwner) {
        await createFilter({ listType: 'block', matchType: 'email', matchValue: fromEmail });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['filters'] });
      onActioned?.();
    }
  });

  // Mark as not-spam: train Rspamd as ham + add sender to allow list
  const notSpamMutation = useMutation({
    mutationFn: async () => {
      if (!mailboxId || uid === null || !data?.message?.fromEmail) return Promise.reject();
      const fromEmail = data.message.fromEmail;
      await markSpam(mailboxId, uid, false, folder);
      await createFilter({ listType: 'allow', matchType: 'email', matchValue: fromEmail });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['filters'] });
      onActioned?.();
    }
  });

  const message = data?.message;
  const { mailboxes } = useMailStore();

  // The server found this email under another uid and dropped the stale duplicate — refresh the
  // lists so it disappears (see cache-relocate.js on the backend).
  const replacedBy = message?.replacedBy ?? null;
  useEffect(() => {
    if (replacedBy == null) return;
    queryClient.invalidateQueries({ queryKey: ['messages'] });
    queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
  }, [replacedBy, queryClient]);

  // Opening a message marks it read (standard mail behavior) so it visibly moves to
  // the read style (gray) in the list.
  useEffect(() => {
    if (message && !message.isRead) markMutation.mutate({ isRead: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.mailboxId, message?.uid, message?.folder]);

  // Fit wide (fixed-layout) HTML emails into the mobile reading pane: scale the body
  // down to the container width instead of clipping or side-scrolling. Re-fits when
  // the message changes, images load, or the viewport resizes.
  const bodyWrapRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // 🔴 React 19 compares the dangerouslySetInnerHTML OBJECT, not its string: a fresh {__html} on
  // every render rewrote the body each time anything re-rendered (a refetch, opening the contact
  // menu) and wiped the links linkify had added. The same object is reused while the HTML is the same.
  const bodyHtmlProp = useRef<{ __html: string }>({ __html: '' });
  useEffect(() => {
    const wrap = bodyWrapRef.current;
    const bodyEl = bodyRef.current;
    if (!wrap || !bodyEl) return;
    const fit = () => {
      bodyEl.style.transform = '';
      bodyEl.style.width = '';
      wrap.style.height = '';
      const containerW = wrap.clientWidth;
      const contentW = bodyEl.scrollWidth;
      if (containerW > 0 && contentW > containerW + 2) {
        const scale = containerW / contentW;
        bodyEl.style.transformOrigin = 'top left';
        bodyEl.style.transform = `scale(${scale})`;
        bodyEl.style.width = `${contentW}px`;
        wrap.style.height = `${bodyEl.scrollHeight * scale}px`;
        wrap.style.overflowX = 'hidden';
      } else {
        wrap.style.overflowX = '';
      }
    };
    const raf = requestAnimationFrame(fit);
    const imgs = Array.from(bodyEl.querySelectorAll('img'));
    imgs.forEach((img) => img.addEventListener('load', fit));
    window.addEventListener('resize', fit);
    return () => {
      cancelAnimationFrame(raf);
      imgs.forEach((img) => img.removeEventListener('load', fit));
      window.removeEventListener('resize', fit);
    };
  }, [message, imagesLoaded, loadImagesByDefault]);

  // Linkify emails + phone numbers in the rendered email body so the user can
  // tap to compose / iMessage. Runs in its own effect (separate from the fit
  // effect) so it's tied to the rendered HTML, not viewport scaling. Runs after
  // React commits the dangerouslySetInnerHTML DOM so it walks real nodes.
  // Depends on message + imagesLoaded + loadImagesByDefault (the same inputs that
  // determine renderedHtml) so it re-runs when the body content changes.
  useEffect(() => {
    if (!bodyRef.current || !message) return;
    linkifyEmailsAndPhones(bodyRef.current);
  }, [message, imagesLoaded, loadImagesByDefault]);

  // A tap on a phone or address link — linkified, or the sender's own mailto:/tel:/sms: — opens
  // the action menu instead of the link. Modified clicks (new tab, etc.) keep the plain link.
  const onBodyClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element).closest?.('a');
    if (!a || !e.currentTarget.contains(a)) return;
    const link = contactFromHref(a.getAttribute('href'), a.textContent || '');
    if (!link) return;
    e.preventDefault();
    const r = a.getBoundingClientRect();
    setContact({ link, anchor: { x: r.left + r.width / 2, top: r.top, bottom: r.bottom } });
  };

  if (!mailboxId || uid === null) {
    return (
      <div className="flex h-full items-center justify-center text-bmail-text-secondary">
        Select a message to read
      </div>
    );
  }

  if (isLoading) {
    return null; // the connection pill shows while it loads
  }

  if (!message) {
    // Distinguish "Google connection expired" (fixable via Reconnect) from a genuinely missing message
    const authErr = /OAuth|token/i.test(String((error as Error)?.message || ''));
    const mailbox = mailboxes.find((m) => m.id === mailboxId);
    if (authErr || mailbox?.authFailed) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-sm font-medium text-bmail-error">This mailbox lost its Google connection.</p>
          <p className="text-xs text-bmail-text-secondary">Reconnect to restore access to these messages.</p>
          <button
            onClick={() => { window.location.href = `${API_BASE_URL}/oauth/gmail/reauth/${mailboxId}`; }}
            className="rounded-lg bg-bmail-goldfill px-4 py-2 text-xs font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover"
          >
            Reconnect {mailbox?.email || 'Google'}
          </button>
        </div>
      );
    }
    return <div className="flex h-full items-center justify-center text-bmail-text-secondary">Message not found</div>;
  }

  const bodyHtml = message.bodyHtml ? sanitizeEmailHtml(message.bodyHtml) : null;
  const bodyText = message.bodyText || (message.bodyHtml ? stripHtml(message.bodyHtml) : '');
  // The sanitizer stashes remote image URLs on data-src. Show them by default (per the
  // user setting) or when the user clicks "Download images"; otherwise leave them hidden.
  // Only auto-load remote images for CLEAN mail — never spam/review (tracking pixels
  // confirm your address + leak your IP). The user can still click "Download images".
  const showImages = (loadImagesByDefault && message.spamAction === 'clean') || imagesLoaded;
  const hasBlockedImages = !!bodyHtml && /<img[^>]+data-src=/i.test(bodyHtml);
  let renderedHtml: string = bodyHtml ?? '';
  if (bodyHtml && showImages) {
    // Re-enable via the DOM (only img[data-src], only http/https) rather than a string
    // replace, so the sanitizer's URI-scheme allowlist can't be laundered through data-src.
    const doc = new DOMParser().parseFromString(bodyHtml, 'text/html');
    doc.querySelectorAll('img[data-src]').forEach((img) => {
      const u = img.getAttribute('data-src') || '';
      if (/^https?:\/\//i.test(u)) img.setAttribute('src', u);
    });
    renderedHtml = doc.body.innerHTML;
  }

  return (
    <div className="flex h-full min-w-0 flex-col overflow-x-hidden">
      {/* Top toolbar */}
      <div className="flex items-center justify-between border-b border-bmail-border px-4 py-3 md:px-6">
        <div className="flex items-center gap-1 md:gap-2 ml-auto">
          <button
            onClick={() => markMutation.mutate({ isRead: !message.isRead })}
            className="rounded-lg p-2 text-bmail-text hover:bg-bmail-surface-hover"
            title={message.isRead ? 'Mark unread' : 'Mark read'}
          >
            {message.isRead ? <MailOpen size={18} /> : <MailOpen size={18} className="fill-current" />}
          </button>
          <button
            onClick={() => markMutation.mutate({ isFlagged: !message.isFlagged })}
            className={`rounded-lg p-2 hover:bg-bmail-surface-hover ${message.isFlagged ? 'text-bmail-gold' : 'text-bmail-text'}`}
            title="Flag"
          >
            <Flag size={18} className={message.isFlagged ? 'fill-current' : ''} />
          </button>
          <button
            onClick={() => navigate(`/compose?reply=${encodeURIComponent(`${message.mailboxId}/${message.uid}/${message.folder || folder || ''}`)}`)}
            className="rounded-lg p-2 text-bmail-text hover:bg-bmail-surface-hover"
            title="Reply"
            aria-label="Reply"
          >
            <Reply size={18} />
          </button>
          <button
            onClick={() => navigate(`/compose?reply=${encodeURIComponent(`${message.mailboxId}/${message.uid}/${message.folder || folder || ''}`)}&all=1`)}
            className="rounded-lg p-2 text-bmail-text hover:bg-bmail-surface-hover"
            title="Reply all"
            aria-label="Reply all"
          >
            <ReplyAll size={18} />
          </button>
          <button
            onClick={() => navigate(`/compose?forward=${encodeURIComponent(`${message.mailboxId}/${message.uid}/${message.folder || folder || ''}`)}`)}
            className="rounded-lg p-2 text-bmail-text hover:bg-bmail-surface-hover"
            title="Forward"
          >
            <Forward size={18} />
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            className="rounded-lg p-2 text-bmail-error hover:bg-bmail-error/10"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Spam action bar — buttons centered, badge moved to banner below */}
      <div className="flex items-center justify-center gap-2 border-b border-bmail-border bg-bmail-surface px-4 py-2 md:px-6">
        {/* Not spam → train as ham + allow sender */}
        <button
          onClick={() => notSpamMutation.mutate()}
          disabled={notSpamMutation.isPending}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-green-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50 min-w-[110px]"
          title="Mark as not spam and allow this sender"
        >
          <CheckCircle2 size={14} />
          {notSpamMutation.isPending ? '…' : 'Not Spam'}
        </button>
        {/* Block sender → train as spam + block */}
        <button
          onClick={() => spamMutation.mutate()}
          disabled={spamMutation.isPending}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50 min-w-[110px]"
          title="Mark as spam and block this sender"
        >
          <Ban size={14} />
          {spamMutation.isPending ? '…' : 'Block'}
        </button>
        {/* Unsubscribe → auto-follow unsubscribe links */}
        <button
          onClick={() => { setShowUnsubscribe(true); setUnsubPending(true); }}
          disabled={unsubPending}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-purple-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-50 min-w-[110px]"
          title="Find and follow unsubscribe link in this email"
        >
          <MailX size={14} />
          {unsubPending ? '…' : 'Unsubscribe'}
        </button>
      </div>

      {/* Spam classification banner — below the action buttons */}
      {message.spamAction === 'spam' && (
        <div className="flex items-center justify-center gap-1.5 border-b border-bmail-border bg-bmail-error/5 px-4 py-1.5 text-xs font-medium text-bmail-error md:px-6">
          <ShieldAlert size={14} /> Spam
        </div>
      )}
      {message.spamAction === 'review' && (
        <div className="flex items-center justify-center gap-1.5 border-b border-bmail-border bg-bmail-warning/5 px-4 py-1.5 text-xs font-medium text-bmail-warning md:px-6">
          <ShieldCheck size={14} /> Might be Spam
        </div>
      )}
      {message.spamAction === 'clean' && (
        <div className="flex items-center justify-center gap-1.5 border-b border-bmail-border bg-bmail-surface px-4 py-1.5 text-xs font-medium text-bmail-text-secondary md:px-6">
          <ShieldCheck size={14} /> Clean
        </div>
      )}

      {/* Message body */}
      <div className="min-w-0 flex-1 overflow-y-auto px-4 py-4 md:px-6">
        <div className="mb-4">
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-lg font-semibold text-bmail-text md:text-xl">{message.subject && message.subject.trim() && message.subject.trim() !== 'Re:' && message.subject.trim() !== 'Fwd:' ? message.subject : '(no subject)'}</h2>

          </div>

          {/* Detailed headers */}
          <div className="mt-3 space-y-1 rounded-lg border border-bmail-border bg-bmail-surface/50 px-4 py-3 text-sm">
            <div className="flex gap-2">
              <span className="w-16 shrink-0 font-medium text-bmail-text-secondary">From:</span>
              <span className="text-bmail-text">
                {message.fromName && <span className="font-medium">{message.fromName} </span>}
                {message.fromEmail && <span className="text-bmail-text-secondary">&lt;{message.fromEmail}&gt;</span>}
              </span>
            </div>
            <div className="flex gap-2">
              <span className="w-16 shrink-0 font-medium text-bmail-text-secondary">To:</span>
              {formatAddrs(message.toEmail)
                ? <span className="text-bmail-text">{formatAddrs(message.toEmail)}</span>
                : <span className="italic text-bmail-text-secondary">Bulk send</span>}
            </div>
            {formatAddrs(message.ccEmail) && (
              <div className="flex gap-2">
                <span className="w-16 shrink-0 font-medium text-bmail-text-secondary">Cc:</span>
                <span className="text-bmail-text">{formatAddrs(message.ccEmail)}</span>
              </div>
            )}
            <div className="flex gap-2">
              <span className="w-16 shrink-0 font-medium text-bmail-text-secondary">Date:</span>
              <span className="text-bmail-text">{formatDateTime(message.date)}</span>
            </div>
          </div>

          {/* Attachments — at the top, impossible to miss (moved 2026-09-01; used
              to live below the body where they read as an afterthought) */}
          {(message.attachments || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-bmail-gold/40 bg-bmail-surface px-3 py-2.5">
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-bmail-text">
                <Download size={13} className="text-bmail-gold" />
                Attachments ({(message.attachments || []).length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {message.attachments?.map((a, i) => (
                  <button
                    key={`${a.filename}-${i}`}
                    onClick={(e) => downloadAttachment(mailboxId, uid, i, a.filename, folder)}
                    className="flex items-center gap-2 rounded-lg border border-bmail-border bg-bmail-navy px-3 py-2 text-xs text-bmail-text hover:bg-bmail-surface-hover transition-colors"
                    title={`Download ${a.filename}`}
                  >
                    <Download size={14} className="shrink-0 text-bmail-gold" />
                    <span className="truncate max-w-[180px] font-medium">{a.filename}</span>
                    <span className="text-bmail-text-secondary">({Math.round((a.size || 0) / 1024)} KB)</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {(message.spamAction === 'spam' || message.spamAction === 'review') && formatSymbols(message.spamSymbols) && (
          <div className="mt-3 rounded-lg border border-bmail-border bg-bmail-surface/50 px-3 py-2 text-xs text-bmail-text-secondary">
            <span className="font-medium text-bmail-text">Filtered because:</span> {formatSymbols(message.spamSymbols)}
          </div>
        )}

        {bodyHtml ? (
          <>
            {hasBlockedImages && !showImages && (
              <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm">
                <span className="text-bmail-text-secondary">Images in this email are hidden.</span>
                <button
                  onClick={() => setImagesLoaded(true)}
                  className="shrink-0 rounded-md bg-bmail-goldfill px-3 py-1 font-medium text-black hover:opacity-90"
                >
                  Download images
                </button>
              </div>
            )}
            {/* Wide fixed-layout HTML emails are scaled to fit the pane (see the fit
                effect above); falls back to horizontal scroll if scaling is off.
                B-22: contain:paint keeps the email inside this box. Its inline styles survive
                sanitizing, and position:fixed could otherwise cover the app (a fake sign-in). */}
            <div ref={bodyWrapRef} onClick={onBodyClick} className="max-w-full overflow-x-auto [&_img]:max-w-full" style={{ contain: 'paint', position: 'relative' }}>
              <div
                ref={bodyRef}
                className="email-body max-w-none text-bmail-text [&_a]:break-words [&_pre]:whitespace-pre-wrap [&_pre]:break-words"
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                dangerouslySetInnerHTML={bodyHtmlProp.current.__html === renderedHtml ? bodyHtmlProp.current : (bodyHtmlProp.current = { __html: renderedHtml })}
              />
            </div>
          </>
        ) : (
          <>
            {message.bodyStale && (
              <div className="mb-3 rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text-secondary">
                🐝 {replacedBy != null
                  ? 'This was an old copy of the email. The current one is in your list; here is the saved text.'
                  : 'The mail server no longer has this copy (it may have been moved or deleted elsewhere). Showing the saved text.'}
              </div>
            )}
            {bodyText
              ? <pre onClick={onBodyClick} className="whitespace-pre-wrap break-words text-sm text-bmail-text [&_a]:text-bmail-gold [&_a]:underline" style={{ overflowWrap: 'anywhere' }}>{withContactLinks(bodyText)}</pre>
              : <p className="text-sm italic text-bmail-text-secondary">This email has no text content.</p>}
          </>
        )}
      </div>

      {/* Contact link menu + forward picker: outside the email box, whose contain:paint would clip them. */}
      {contact && (
        <ContactActionPopover
          link={contact.link}
          anchor={contact.anchor}
          onClose={() => setContact(null)}
          onNewMessage={(address) => { setContact(null); navigate(composeToUrl(address, { from: mailboxId || undefined })); }}
          onForward={(address) => { setContact(null); setForwardTo(address); }}
          onCopyAddress={copyAddress}
        />
      )}
      {forwardTo && (
        <ForwardPickerModal
          to={forwardTo}
          onClose={() => setForwardTo(null)}
          onPick={(p) => { const to = forwardTo; setForwardTo(null); navigate(composeToUrl(to, { forward: p })); }}
        />
      )}

      {/* Unsubscribe modal */}
      {showUnsubscribe && message && mailboxId && uid !== null && (
        <UnsubscribeModal
          mailboxId={mailboxId}
          uid={uid}
          folder={message.folder || folder}
          fromEmail={message.fromEmail || ''}
          fromName={message.fromName || undefined}
          subject={message.subject || undefined}
          onClose={() => { setShowUnsubscribe(false); setUnsubPending(false); }}
        />
      )}
    </div>
  );
}